import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Route } from '../../../enums';
import { useAuthStore } from '../../../store/auth.store';
import { api } from '../../../services/api';
import type { Principal } from '../../../types';

/**
 * Lands here after SSO at `/signed-in?next=<path>`, with nothing secret in the URL. The server's callback has just
 * set the refresh cookie; this page exchanges it for an access token and continues to `next` (a path on this
 * portal only, e.g. the authorize page an agent opened) or to Apps.
 */
export function AuthCallbackPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const setSession = useAuthStore((s) => s.setSession);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    api
      .post<{ accessToken: string; principal: Principal }>('/auth/refresh')
      .then((r) => {
        setSession(r.accessToken, r.principal);
        navigate(safeNext(params.get('next')), { replace: true });
      })
      .catch(() => setFailed(true));
  }, [setSession, navigate, params]);

  if (failed) return <Navigate to={Route.Login} replace />;
  return null;
}

/** Only a path on this portal: never another origin, never a protocol-relative URL. */
export function safeNext(next: string | null): string {
  if (!next || !next.startsWith('/') || next.startsWith('//') || next.startsWith('/signed-in') || next.startsWith('/login')) return Route.Apps;
  return next;
}
